import VcImage from '@components/atoms/vc-images/vc-image';
import { ListingContext } from '@context/listing.context';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import styles from './index.module.scss';

interface ModelVariationsPropsInterface {
  isDesktop: boolean;
  setIsModelsModalOpen: (open: boolean) => void;
}

const ModelVariations: React.FC<ModelVariationsPropsInterface> = ({ isDesktop, setIsModelsModalOpen }) => {
  const {
    models,
    brandLabel,
    modelLabel,
    listingDraftPayload: { macroModelId, modelVariationId },
    setListingDraftAttribute,
  } = useContextSelector(ListingContext, (state) => state);
  const { t } = useTranslation('pricing-calculator');

  const model = useMemo(() => {
    return models.find((i) => i.id === macroModelId);
  }, [macroModelId, models]);

  const imgSize = isDesktop ? 96 : 56;

  if (!model) {
    return null;
  }

  return (
    <>
      <div className={styles.modelVariations}>
        <div className={styles.modelVariations__label}>
          <div
            id="selecteMoodelVariationsLabel"
            className={clsx('vc-title-s', styles.modelVariations__label__title)}
          >
            {t('PRICING_CALCULATOR.MODEL_SELECTION.VARIATIONS.TITLE')}
          </div>
          <div className={styles.modelVariations__label__model}>
            <div>
              <VcImage
                width={imgSize}
                height={imgSize}
                src={`/marque/modele/${model.picture}`}
                alt={model.displayName || model.id}
                className={styles.modelVariations__label__img}
              />
            </div>
            <div className={styles.modelVariations__label__resume}>
              <span className={styles.modelVariations__label__brandName}>{brandLabel}</span>
              <span className={styles.modelVariations__label__modelName}>{modelLabel}</span>
            </div>
          </div>
        </div>
        <ul
          className={styles.modelVariations__list}
          role="listbox"
          aria-labelledby="selecteMoodelVariationsLabel"
        >
          {model.variations &&
            model.variations[0]?.list.map((variation) => (
              <li
                key={`variation-${variation.id}`}
                role="option"
                className={clsx(
                  styles.modelVariations__list__item,
                  modelVariationId === variation?.id && styles.selected,
                )}
                onClick={() => {
                  setListingDraftAttribute({ modelVariationId: variation.id });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setListingDraftAttribute({
                      modelVariationId: variation.id,
                    });
                  }
                }}
                aria-selected={Boolean(modelVariationId === variation?.id)}
              >
                <span>
                  {variation.title}
                  <span className={styles.modelVariations__list__item__description}>{` ${variation.description}`}</span>
                </span>
                <span className={styles.modelVariations__list__item__radio} />
              </li>
            ))}
          <li
            role="option"
            className={clsx(styles.modelVariations__list__item, !modelVariationId && styles.selected)}
            onClick={() => {
              setListingDraftAttribute({ modelVariationId: '' });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setListingDraftAttribute({ modelVariationId: '' });
              }
            }}
            aria-selected={Boolean(!modelVariationId)}
          >
            <span>{t('PRICING_CALCULATOR.MODEL_SELECTION.VARIATIONS.OTHER')}</span>
            <span className={styles.modelVariations__list__item__radio} />
          </li>
        </ul>
        <button
          type="button"
          className={clsx(
            styles.modelVariations__cta,
            'vc-btn vc-btn--primary vc-btn--large vc-btn--full vc-btn--notfull--md-up',
          )}
          onClick={() => setIsModelsModalOpen(false)}
        >
          {t('PRICING_CALCULATOR.MODEL_SELECTION.VARIATIONS.CTA')}
        </button>
      </div>
    </>
  );
};

export default ModelVariations;
